/**
 * "👷 What the tech will do" — one block the office and the tech both read,
 * word for word (server: routes/jobTechPlan.ts, services/techPlan).
 */
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";

export type TechAction = "test" | "repair" | "replace";

export interface TechPlanLine {
    key: string;
    assetId: string | null;
    /** Every device this line covers: one, or several identical ones shown once. */
    assetIds?: string[];
    /** Each covered device's own stored words (differs from `text` when grouped). */
    memberText?: string;
    action: TechAction | null;
    number: number;
    text: string;
    where: string | null;
    why: string | null;
    parts: string[];
    /** migration 721: the office changed (or added) this line after dispatch. */
    changedAt?: string | null;
    /** What the line said before that change (null for an added line). */
    was?: string | null;
}

export interface TechPlanAutoLine {
    icon: string;
    text: string;
    from: "last visit" | "the schedule" | "the site";
}

export interface TechPlanTechView {
    jobId: string;
    kind: "test" | "repair" | "replace" | "mixed";
    lines: TechPlanLine[];
    jobWide: Array<{ icon: string; text: string }>;
    autoLines: TechPlanAutoLine[];
    workOrderText: string | null;
    changedByOffice: { at: string; atWords: string; byName: string | null } | null;
}

export interface TechPlanOfficeView extends Omit<TechPlanTechView, "lines"> {
    lines: Array<TechPlanLine & { checks: string[] }>;
    saved: boolean;
    jobWideText: string;
    sources: string[];
    beforeItGoes: string[];
    officeOnly: string[];
    devices: Array<{ assetId: string; action: TechAction; label: string }>;
}

export type TechPlanResponse =
    { audience: "office"; plan: TechPlanOfficeView } | { audience: "tech"; plan: TechPlanTechView };

export const techPlanKey = (jobId: string, as: "auto" | "tech") => ["job", jobId, "tech-plan", as] as const;

/** The block for the signed-in user: the office gets the office view, a tech the tech view. */
export function useTechPlan(jobId: string | null | undefined, opts: { asTech?: boolean; enabled?: boolean } = {}) {
    const as = opts.asTech ? "tech" : "auto";
    return useQuery({
        queryKey: techPlanKey(jobId ?? "", as),
        queryFn: () => api.get<TechPlanResponse>(`/api/jobs/${jobId}/tech-plan${opts.asTech ? "?as=tech" : ""}`),
        enabled: !!jobId && opts.enabled !== false,
        staleTime: 30_000,
    });
}

/** The chip on each line: what the visit does to the device. Never "Not tested" on a repair. */
export const ACTION_CHIP: Record<TechAction, { label: string; className: string }> = {
    repair: { label: "Repair", className: "bg-amber-100 text-amber-900" },
    replace: { label: "Replace", className: "bg-blue-100 text-blue-900" },
    test: { label: "Test", className: "bg-stone-200 text-stone-700" },
};
